import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import React from "react";
import { Controller, get, useFormContext } from "react-hook-form";

interface SelectFieldProps {
  fieldName: string;
  label?: string;
  fullWidth?: boolean;
  control?: any;
  error?: boolean;
  helperText?: any;
  defaultValue?: string | boolean | null | number | any[];
  options: any[];
  variant?: "filled" | "outlined" | "standard";
  valueKey: string;
  labelKey: string;
  disabled?: boolean;
  onFieldChange?: (event: React.ChangeEvent<{ value: unknown }>) => void;
  other?: object;
  labelManipulateFn?: (label: string) => string;
  style?: object;
  onChangeFunction?: (option: any) => void;
  emptyOptionLabel?: string | null;
  multiple?: boolean; // NEW PROP
  renderOption?: (option: any) => React.ReactNode;
  showMissingDeleted?: boolean;
  currentValue?: any;
  showEmptyOption?: boolean;
  // optional: provide yup schema so component can show required asterisk on mount
  yupSchema?: any;
  required?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  fieldName,
  control,
  label,
  error: errorBool = false,
  helperText,
  fullWidth,
  defaultValue,
  options,
  variant = "outlined",
  valueKey,
  labelKey,
  disabled,
  onFieldChange,
  other,
  labelManipulateFn,
  style,
  onChangeFunction,
  emptyOptionLabel = "None",
  multiple = false,
  renderOption = null,
  showMissingDeleted = false,
  currentValue,
  showEmptyOption = true,
  yupSchema,
  required = false,
}) => {
  const methods = useFormContext();

  const {
    control: formControl,
    watch,
    setValue: setField,
    getValues,
    trigger,
    formState: { errors },
  } = methods || {};

  // useEffect(() => {
  //   if (options?.length === 1 && !getValues(fieldName)) {
  //     setField?.(fieldName, options?.[0]?.[valueKey]);
  //   }
  // }, [options?.[0]]);

  const computeIsRequired = React.useCallback(() => {
    if (required) return true;
    try {
      if (yupSchema) {
        if (typeof yupSchema?.describe === "function") {
          const desc = yupSchema?.describe();
          const f = desc?.fields?.[fieldName];
          if (f?.tests)
            return f.tests?.some((t: any) => t?.name === "required");
        }
        const fieldSchema =
          yupSchema?.fields?.[fieldName] ?? yupSchema?.[fieldName];
        if (fieldSchema) {
          const tests = fieldSchema?.tests ?? fieldSchema?._tests ?? [];
          if (Array.isArray(tests))
            return tests?.some(
              (t: any) => t?.name === "required" || t?.TEST === "required"
            );
        }
      }
    } catch {
      /* ignore */
    }
    try {
      const fieldState = (methods as any)?.getFieldState
        ? (methods as any).getFieldState(fieldName)
        : null;
      return !!fieldState?.invalid;
    } catch {
      return false;
    }
  }, [required, yupSchema, fieldName, methods]);

  const [isRequiredState, setIsRequiredState] = React.useState<boolean>(() =>
    computeIsRequired()
  );
  React.useEffect(() => {
    setIsRequiredState(computeIsRequired());
  }, [computeIsRequired]);

  const labelWithAsterisk = !label
    ? ""
    : isRequiredState
    ? `${label} *`
    : label;

  const emptyValue = emptyOptionLabel === "All" ? 0 : "";

  const isValueMissing =
    showMissingDeleted &&
    currentValue &&
    !multiple &&
    options &&
    !options?.some(
      (option) => String(option[valueKey]) === String(currentValue)
    );

  const watchedValue = watch ? watch(fieldName) : undefined;

  // Combine error messages
  const displayHelperText = isValueMissing ? "Missing/Deleted" : helperText;
  const error = errorBool || get(errors || {}, fieldName);

  const commonTextFieldStyles = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: error ? "0px" : "2px",
      borderColor: error ? "transparent" : "primary.main",
      borderRadius: "8px",
      color: "secondary.main",
    },
    height: multiple ? "auto" : 38,
    backgroundColor: "rgba(63, 141, 124, 0.00)",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: error ? "transparent" : "primary.main",
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.85)",
    },
    ...style,
  };

  React.useEffect(() => {
    if (!trigger || !watchedValue) return;
    trigger(fieldName);
  }, [watchedValue, fieldName, trigger]);

  return (
    <FormControl
      fullWidth={fullWidth !== false}
      variant={variant}
      error={error || isValueMissing}
      disabled={disabled ?? false}
    >
      {!!label && (
        <InputLabel
          sx={{
            color:
              !!get(errors || {}, fieldName) || error || isValueMissing
                ? "error.main"
                : "secondary.main",
            background: "none",
            padding: "0 5px",
          }}
        >
          {labelWithAsterisk}
        </InputLabel>
      )}
      <Controller
        name={fieldName}
        control={control ?? formControl}
        defaultValue={defaultValue || (multiple ? [] : emptyValue)}
        render={({ field }) => {
          const displayOptions = [...(options || [])];
          return (
            <>
              <Select
                {...field}
                multiple={multiple}
                value={
                  multiple
                    ? Array.isArray(field.value)
                      ? field.value
                      : []
                    : field.value ?? emptyValue
                }
                onChange={(event) => {
                  const value = event.target.value;
                  onFieldChange?.(value);
                  field.onChange(value);
                }}
                sx={commonTextFieldStyles}
                label={labelWithAsterisk}
                {...other}
              >
                {!multiple &&
                  showEmptyOption &&
                  emptyOptionLabel && ( // Modified this line
                    <MenuItem value={emptyValue}>{emptyOptionLabel}</MenuItem>
                  )}
                {displayOptions?.map((option, index) => (
                  <MenuItem
                    key={`${option[valueKey]}-${index}`}
                    value={option[valueKey]}
                    onClick={() => onChangeFunction?.(option)}
                    sx={
                      option[labelKey] === "Missing/Deleted"
                        ? {
                            color: "error.main",
                            fontStyle: "italic",
                          }
                        : {}
                    }
                  >
                    {renderOption
                      ? renderOption(option)
                      : labelManipulateFn
                      ? labelManipulateFn(option[labelKey])
                      : option[labelKey]}
                  </MenuItem>
                ))}
              </Select>
              {!!get(errors || {}, fieldName) || error || isValueMissing ? (
                <FormHelperText
                  sx={{
                    color: "error.main",
                  }}
                >
                  {displayHelperText ??
                    get(errors || {}, fieldName)?.message?.toString()}
                </FormHelperText>
              ) : null}
            </>
          );
        }}
      />
    </FormControl>
  );
};

export default SelectField;
